/**
 * Gate a screen on a permission.
 *
 * A user who reaches a screen they may not see is told so plainly, rather
 * than being shown an empty page or a misleading "not found". Pages are lazy,
 * so this also provides the loading fallback.
 */
import { Suspense } from 'react';
import type { Action } from '@rsc/shared';
import { useSession } from '@/stores/session';
import { PermissionDenied, Spinner } from './states';

export function RequirePermission({
  module,
  action = 'view',
  what,
  children,
}: {
  module: string;
  action?: Action;
  what?: string;
  children: React.ReactNode;
}) {
  const can = useSession((s) => s.can);
  if (!can(module, action)) return <PermissionDenied what={what} />;
  return <Suspense fallback={<Spinner label="Loading…" />}>{children}</Suspense>;
}

/** Gate a screen that more than one permission opens (e.g. a master shared by two modules). */
export function RequireAnyPermission({
  modules,
  action = 'view',
  what,
  children,
}: {
  modules: string[];
  action?: Action;
  what?: string;
  children: React.ReactNode;
}) {
  const can = useSession((s) => s.can);
  if (!modules.some((m) => can(m, action))) return <PermissionDenied what={what} />;
  return <Suspense fallback={<Spinner label="Loading…" />}>{children}</Suspense>;
}
